import moment from 'moment';

import { getProxyAuthHeaders } from '@/auth/imsTokenStorage';
import { config } from '@/config/env';
import { logger } from '@/logger';

import { RestApi } from './RestApi.ts';

export class ExpressApiError extends Error {
	public statusCode: number;

	constructor(message: string, statusCode: number) {
		super(message);
		this.name = 'ExpressApiError';
		this.statusCode = statusCode;
	}
}

export type ChannelType =
	| 'facebook'
	| 'instagram'
	| 'twitter'
	| 'pinterest'
	| 'linkedin'
	| 'tiktok';

export interface ChannelConnection {
	id: string;
	name: string;
	type: 'page' | 'group' | 'profile' | 'business' | 'creator' | 'unknown';
	expires: string;
	pictureUrl?: string | null;
}

export interface Channel {
	id: string;
	name: string;
	type: ChannelType;
	order: number;
	deletedAt: string | null;
	isActive: boolean;
	connectionUpdateAt?: string;
	connection?: ChannelConnection;
	previousConnection?: {
		name: string;
		pictureUrl?: string | null;
	};
}

type CreateChannelResponse = Pick<
	Channel,
	'id' | 'name' | 'type' | 'order' | 'deletedAt'
>;

type ContentDelta = {
	insert: string;
	attributes?: {
		italic?: boolean;
		bold?: boolean;
		underline?: boolean;
		strike?: boolean;
		link?: string;
	};
};

interface AttachmentBody {
	id: string;
}

export interface CreatePostPayload {
	contentDelta: ContentDelta[];
	channel: { id: string };
	attachments: AttachmentBody[];
	location: string;
	metadata?: {
		publishType: 'schedule' | 'publishNow';
		firstCommentDelta: ContentDelta[];
		instagram?: { type: string };
	};
	state: 'approved';
	linkPreview: null;
	publishDate: string;
	copyAttachments?: AttachmentBody[];
}

export interface CreatePostResponse extends CreatePostPayload {
	id: string;
}

interface UpdatePostPayload {
	contentDelta: ContentDelta[];
	channel: { id: string };
	attachments: AttachmentBody[];
	location: 'calendar';
	metadata: {
		publishType: 'schedule' | 'publishNow';
		firstCommentDelta: ContentDelta[];
		instagram?: { type: string };
	};
	state: 'approved';
	linkPreview: null;
	publishDate: string;
	copyAttachments?: Array<{
		source: {
			calendarId: string;
			projectId: string;
		};
		mediaIds: string[];
	}>;
}

export interface Calendar {
	verifiedDomains: string[];
	state: string;
	id: string;
	name: string;
	channels: Channel[];
	channelsLimits: Record<string, number>;
}

export const checkHasValidInstagramConnection = (
	calendars:
		| Array<{
				channels: Array<{
					type: string;
					connection?: { expires: string };
				}>;
		  }>
		| undefined,
): boolean => {
	const calendar = calendars?.[0];
	if (!calendar) return false;

	const channel = calendar.channels.find(
		(ch) => ch.type === 'instagram' && ch.connection,
	);
	if (!channel?.connection) return false;

	return new Date(channel.connection.expires) > new Date();
};

export const getInstagramChannelFromCalendars = (
	calendars:
		| Array<{ id: string; name: string; channels: Channel[] }>
		| undefined,
): { calendarId: string; channelId: string } | null => {
	const calendar = calendars?.[0];
	if (!calendar) return null;

	const channel = calendar.channels.find((ch) => ch.type === 'instagram');
	if (!channel) return null;

	return { calendarId: calendar.id, channelId: channel.id };
};

export class ExpressAPI extends RestApi {
	private accessToken: string | null = null;

	constructor(imsToken: string) {
		super(`${config.UI_PROXY_URL}/express-vite-proxy`);
		this.accessToken = imsToken;
	}

	private getAuthHeaders = () => getProxyAuthHeaders(this.accessToken);

	public getCalendars = async () => {
		return this.request<Calendar[]>(
			'GET',
			'/calendars',
			undefined,
			this.getAuthHeaders(),
		);
	};

	public createCalendarChannel = async (
		calendarId: string,
		channelType: ChannelType,
		name: string,
	): Promise<Channel> => {
		const response = await fetch(
			`${config.EXPRESS_API_HOST}/calendars/${calendarId}/channels`,
			{
				method: 'POST',
				headers: {
					'x-api-key': config.EXPRESS_API_KEY,
					...this.getAuthHeaders(),
					'Content-Type': 'application/json',
				},
				credentials: 'include',
				body: JSON.stringify({ type: channelType, name }),
			},
		);

		if (!response.ok) {
			const errorText = await response.text();
			logger.error(
				`Failed to create calendar channel: ${response.status} ${response.statusText}`,
			);

			let errorMessage = `Failed to create channel: ${response.statusText}`;
			try {
				const errorJson = JSON.parse(errorText) as { message?: string };
				if (errorJson.message) {
					errorMessage = errorJson.message;
				}
			} catch {
				// ignore
			}

			throw new ExpressApiError(errorMessage, response.status);
		}

		const data = (await response.json()) as CreateChannelResponse;

		return {
			id: data.id,
			name: data.name,
			type: data.type,
			order: data.order,
			deletedAt: data.deletedAt,
			isActive: true,
		};
	};

	public getInstagramChannelInfo = async (): Promise<{
		calendar: Calendar;
		channel: Channel;
	} | null> => {
		let calendars = await this.getCalendars();
		const firstCalendar = calendars[0];
		if (!firstCalendar) return null;

		if (!getInstagramChannelFromCalendars(calendars)) {
			try {
				await this.createCalendarChannel(
					firstCalendar.id,
					'instagram',
					'instagram',
				);
			} catch (err) {
				if (!(err instanceof ExpressApiError && err.statusCode === 409)) {
					throw err;
				}
				logger.info(
					'Instagram channel already exists (409); refetching calendars',
				);
			}
			calendars = await this.getCalendars();
		}

		const channelInfo = getInstagramChannelFromCalendars(calendars);
		if (!channelInfo) return null;

		const calendar = calendars.find((c) => c.id === channelInfo.calendarId);
		const channel = calendar?.channels.find(
			(ch) => ch.id === channelInfo.channelId,
		);
		if (!calendar || !channel) return null;

		return { calendar, channel };
	};

	public deleteConnection = async (
		calendarId: string,
		channelId: string,
	): Promise<void> => {
		const response = await fetch(
			`${config.EXPRESS_API_HOST}/calendars/${calendarId}/channels/${channelId}/connection`,
			{
				method: 'DELETE',
				headers: {
					'x-api-key': config.EXPRESS_API_KEY,
					...this.getAuthHeaders(),
				},
				credentials: 'include',
			},
		);

		if (!response.ok) {
			throw new ExpressApiError(
				`Failed to delete connection: ${response.statusText}`,
				response.status,
			);
		}
	};

	private createPostOnExpress = async (
		calendarId: string,
		posts: CreatePostPayload[],
	) => {
		return this.request<CreatePostResponse[]>(
			'POST',
			`/calendars/${calendarId}/posts`,
			posts,
			this.getAuthHeaders(),
		);
	};

	private updatePostOnExpress = async (
		expressPostId: string,
		post: UpdatePostPayload,
	) => {
		return this.request<void>(
			'PUT',
			`/posts/${expressPostId}`,
			post,
			this.getAuthHeaders(),
		);
	};

	public deletePostFromExpress = async (
		expressPostId: string,
	): Promise<void> => {
		return this.request<void>(
			'DELETE',
			`/posts/${expressPostId}`,
			undefined,
			this.getAuthHeaders(),
			// The caller reads 404 as "Express already dropped it" and continues, so
			// the generic error logging would report a normal outcome to Splunk.
			[404],
		);
	};

	public createMediaWithImageUrl = async (
		imageUrl: string,
		calendarId: string,
	): Promise<{ success: boolean; mediaId: string | null; error?: string }> => {
		try {
			const response = await fetch(`${this.baseUrl}/media-upload`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					...this.getAuthHeaders(),
				},
				body: JSON.stringify({
					imageUrl,
					calendarId,
				}),
			});

			return (await response.json()) as {
				success: boolean;
				mediaId: string | null;
				error?: string;
			};
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : 'Unknown error';
			logger.error('Error uploading media via proxy:', errorMessage);
			return {
				success: false,
				mediaId: null,
				error: errorMessage,
			};
		}
	};

	private buildInstagramPostPayload = (params: {
		caption: string;
		channelId: string;
		mediaIds: string[];
		publishDate: string;
		publishType?: 'schedule' | 'publishNow';
	}): CreatePostPayload => {
		return {
			contentDelta: [{ insert: params.caption }],
			channel: { id: params.channelId },
			attachments: params.mediaIds.map((id) => ({ id })),
			location: 'calendar',
			metadata: {
				publishType: params.publishType || 'schedule',
				firstCommentDelta: [],
				instagram: { type: 'post' },
			},
			state: 'approved',
			linkPreview: null,
			publishDate: moment(params.publishDate).toISOString(),
			copyAttachments: [],
		};
	};

	private buildInstagramUpdatePayload = (params: {
		caption: string;
		channelId: string;
		attachmentIds: string[];
		publishDate: string;
		calendarId: string;
		publishType?: 'schedule' | 'publishNow';
	}): UpdatePostPayload => {
		return {
			contentDelta: [{ insert: params.caption }],
			channel: { id: params.channelId },
			attachments: params.attachmentIds.map((id) => ({ id })),
			location: 'calendar',
			metadata: {
				publishType: params.publishType || 'schedule',
				firstCommentDelta: [],
				instagram: { type: 'post' },
			},
			state: 'approved',
			linkPreview: null,
			publishDate: moment(params.publishDate).toISOString(),
			copyAttachments: [
				{
					source: {
						calendarId: params.calendarId,
						projectId: '',
					},
					mediaIds: params.attachmentIds,
				},
			],
		};
	};

	public createPost = async (params: {
		calendarId: string;
		caption: string;
		channelId: string;
		mediaIds: string[];
		publishDate: string;
		publishType?: 'schedule' | 'publishNow';
	}) => {
		const payload = this.buildInstagramPostPayload({
			caption: params.caption,
			channelId: params.channelId,
			mediaIds: params.mediaIds,
			publishDate: params.publishDate,
			publishType: params.publishType,
		});
		return this.createPostOnExpress(params.calendarId, [payload]);
	};

	public updatePost = async (
		expressPostId: string,
		params: {
			caption: string;
			channelId: string;
			attachmentIds: string[];
			publishDate: string;
			calendarId: string;
			publishType?: 'schedule' | 'publishNow';
		},
	) => {
		const payload = this.buildInstagramUpdatePayload({
			caption: params.caption,
			channelId: params.channelId,
			attachmentIds: params.attachmentIds,
			publishDate: params.publishDate,
			calendarId: params.calendarId,
			publishType: params.publishType,
		});
		return this.updatePostOnExpress(expressPostId, payload);
	};

	public openChannelConnectorPage = (
		calendar: Pick<Calendar, 'id' | 'name' | 'channels'>,
		channelType: ChannelType,
	) => {
		const channel = calendar.channels.find((c) => c.type === channelType);
		if (!channel) {
			throw new Error(
				`Channel ${channelType} not found in calendar ${calendar.id}`,
			);
		}
		const authUrl = `${config.EXPRESS_WEB_HOST}/schedule/connect?step=init&channelType=${channelType}&calendarId=${calendar.id}&channelId=${channel.id}&locale=en-US&mobileShell=false&projectId=personal&backgroundProject=personal`;

		return window.open(
			authUrl,
			'expressChannelConnector',
			'width=600,height=600',
		);
	};

	public initExpressConnection = async (): Promise<Window | null> => {
		try {
			const info = await this.getInstagramChannelInfo();
			if (!info) {
				logger.error(
					'Cannot open Express connector: no calendar/channel found',
				);
				return null;
			}
			return this.openChannelConnectorPage(info.calendar, 'instagram');
		} catch (error) {
			logger.error('Failed to open Express connector:', error);
			return null;
		}
	};
}
